import React, { useState } from 'react';

interface StarRatingProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
  allowHalfStars?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  onRatingChange,
  size = 'md',
  allowHalfStars = false,
}) => {
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  const sizeClasses = {
    sm: 'text-xl',
    md: 'text-2xl',
    lg: 'text-3xl',
  };

  const displayRating = hoverRating !== null ? hoverRating : rating;

  const handleStarClick = (starValue: number) => {
    onRatingChange(starValue);
  };

  const handleStarHover = (starValue: number) => {
    setHoverRating(starValue);
  };

  const handleMouseLeave = () => {
    setHoverRating(null);
  };

  return (
    <div 
      className="flex items-center space-x-1"
      onMouseLeave={handleMouseLeave}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        // Determine what to show based on displayRating (hover or actual rating)
        // Previous stars should be fully highlighted
        const isPreviousStar = star < displayRating;
        // Current star could be full or half
        const isCurrentStarFull = star === displayRating && displayRating % 1 === 0;
        const isCurrentStarHalf = allowHalfStars && displayRating >= star - 0.5 && displayRating < star;
        
        // Check if this specific star is being hovered (for preview)
        const isHoveredHalf = allowHalfStars && hoverRating === star - 0.5;
        const isHoveredFull = hoverRating === star;
        
        const showFull = isPreviousStar || isCurrentStarFull || isHoveredFull;
        const showHalf = isCurrentStarHalf || isHoveredHalf;
        
        return (
          <div key={star} className="relative inline-block" style={{ position: 'relative', width: 'fit-content' }}>
            {/* Background gray star */}
            <span className={`${sizeClasses[size]} text-gray-300`}>
              ★
            </span>
            
            {/* Yellow overlay for full or half star (actual rating or hover preview) */}
            {(showFull || showHalf) && (
              <span 
                className={`${sizeClasses[size]} absolute top-0 left-0 ${
                  hoverRating !== null ? 'text-yellow-300 opacity-80' : 'text-yellow-400'
                }`}
                style={showHalf ? { clipPath: 'inset(0 50% 0 0)' } : {}}
              >
                ★
              </span>
            )}
            
            {/* Clickable areas */}
            {allowHalfStars ? (
              <>
                {/* Left half for 0.5 star */}
                <div
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    handleStarHover(star - 0.5);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStarClick(star - 0.5);
                  }}
                  className="absolute top-0 left-0 bottom-0 cursor-pointer z-30"
                  aria-label={`${star - 0.5} stars`}
                  style={{ 
                    width: '50%',
                    pointerEvents: 'auto'
                  }}
                />
                {/* Right half for full star */}
                <div
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    handleStarHover(star);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStarClick(star);
                  }}
                  className="absolute top-0 right-0 bottom-0 cursor-pointer z-30"
                  aria-label={`${star} stars`}
                  style={{ 
                    width: '50%',
                    pointerEvents: 'auto'
                  }}
                />
              </>
            ) : (
              <div
                onMouseEnter={() => handleStarHover(star)}
                onClick={() => handleStarClick(star)}
                className="absolute inset-0 cursor-pointer z-20"
                aria-label={`${star} stars`}
                style={{ pointerEvents: 'auto' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StarRating;

